import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-40 h-16 px-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5 text-zinc-950" />
        </button>
        <img src={AXIOM_LOGO} alt="Axiom" className="h-8" />
        <div className="w-5" />
      </header>

      <main className="pt-20 px-4 pb-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-bold text-zinc-950 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Privacy Policy</h1>
          <p className="text-sm text-zinc-500 mb-8">Last updated: 1 October 2026</p>

          <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-6">
            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">1. Introduction</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Axiom Recovery Programme ("Axiom", "we", "us" or "our") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store and protect your information in compliance with international privacy laws including GDPR (Europe), HIPAA (USA), PIPEDA (Canada) and Australian Privacy Principles.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">2. Data We Collect</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-2">
                We collect the following personal information:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Account Information:</strong> Email, name, password (encrypted)</li>
                <li><strong>Recovery Data:</strong> Sobriety date/time, journal entries, mental health check-ins</li>
                <li><strong>Meeting Attendance:</strong> Meeting logs, verification photos (selfies)</li>
                <li><strong>Communication:</strong> Messages with administrators, bot conversations</li>
                <li><strong>Progress Tracking:</strong> Week number, task completion status</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">3. How We Use Your Data</h2>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li>Provide the 32-week Recovery programme (2-Week Window plus 30 Programme Weeks)</li>
                <li>Monitor your progress and compliance</li>
                <li>Generate reports for criminal justice system (when required)</li>
                <li>Provide AI-powered sponsor bot support</li>
                <li>Ensure programme accountability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">4. Data Security</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                We implement industry-leading security measures:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Encryption:</strong> All passwords are hashed using bcrypt</li>
                <li><strong>Secure Sessions:</strong> JWT tokens with httpOnly cookies</li>
                <li><strong>Access Control:</strong> Role-based access (User, SuperAdmin only)</li>
                <li><strong>Data Storage:</strong> Encrypted database storage</li>
                <li><strong>HTTPS:</strong> All data transmitted over secure connections</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">5. Data Sharing</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-2">
                We do NOT sell or share your personal data with third parties, except:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Criminal Justice Reports:</strong> Unencrypted reports provided ONLY when legally required</li>
                <li><strong>SuperAdmin Access:</strong> role-based (encrypted data only)</li>
                <li><strong>Anonymized Data:</strong> Statistical data for research (no personal identifiers)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">6. Your Rights</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-2">
                Under GDPR, HIPAA, PIPEDA and Australian Privacy Principles, you have the right to:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update incorrect information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Portability:</strong> Export your data in a standard format</li>
                <li><strong>Objection:</strong> Object to data processing for specific purposes</li>
              </ul>
              <p className="text-sm text-zinc-700 leading-relaxed mt-2">
                To exercise these rights, contact: <strong>support@axiomtudor8.com</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">7. Data Retention</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                We retain your data for:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Active Programme:</strong> Duration of your 32-week Recovery programme</li>
                <li><strong>Post-Programme:</strong> 7 years for criminal justice compliance</li>
                <li><strong>Deletion Requests:</strong> 30 days to process (subject to legal requirements)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">8. Cookies</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                We use essential cookies only:
              </p>
              <ul className="list-disc list-inside text-sm text-zinc-700 leading-relaxed space-y-1">
                <li><strong>Authentication:</strong> Keep you logged in securely</li>
                <li><strong>Preferences:</strong> Remember your settings</li>
              </ul>
              <p className="text-sm text-zinc-700 leading-relaxed mt-2">
                We do NOT use tracking, advertising or analytics cookies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">9. Contact Us</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                For privacy concerns or data requests:
                <br /><strong>Email:</strong> support@axiomtudor8.com
                <br /><strong>Privacy contact:</strong> graeme@tudor8.com
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">10. Changes to This Policy</h2>
              <p className="text-sm text-zinc-700 leading-relaxed">
                We may update this Privacy Policy to reflect changes in our practices or legal requirements. We will notify you of significant changes via email or app notification.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-zinc-950 mb-3">11. Free Five-Day Email Courses</h2>
              <p className="text-sm text-zinc-700 leading-relaxed mb-3">
                We run two free five-day email courses hosted on the same Tudor8 backend, both governed
                by this policy:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-700 leading-relaxed mb-3">
                <li><strong>AIM Family Course</strong> ({' '}<code className="text-xs bg-zinc-100 px-1 rounded">course_stream=family-aim</code>): for family, friends and partners of someone with alcoholism or addiction.</li>
                <li><strong>ARP Individual Course</strong> "Pleasure or Problem?" ({' '}<code className="text-xs bg-zinc-100 px-1 rounded">course_stream=individual-arp</code>): for anyone wondering about their own drinking or drug use.</li>
              </ul>

              <h3 className="text-base font-bold text-zinc-900 mt-4 mb-2">11.1 What we collect at sign-up</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-700 leading-relaxed">
                <li>Email address (required).</li>
                <li>First name (optional).</li>
                <li>Whether you ticked "keep me updated about the programme": stored separately from course delivery.</li>
                <li>UTM source (facebook / linkedin / instagram / x / direct) so we can see which post brought you here: never linked back to your identity in analytics.</li>
                <li>Sign-up timestamp, IP address and browser user-agent, kept as an audit trail for spam-abuse defence.</li>
                <li>For the ARP Individual Course only: your express consent to us holding your email in connection with alcoholism / addiction (Privacy Act 1988 APP 3.3, sensitive information), including the version of the consent language you saw.</li>
              </ul>

              <h3 className="text-base font-bold text-zinc-900 mt-4 mb-2">11.2 What we collect during the course</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-700 leading-relaxed">
                <li>Which day of the sequence you have reached.</li>
                <li>Whether you clicked through to the follow-on programme on Day 5.</li>
                <li>For the ARP Individual Course only: your answers to the optional Day-5 readiness check. Multiple-choice answers are aggregated across all subscribers to see which questions people find hardest; open-text answers are stored for your own reference only and never aggregated. You can view or delete your readiness-check answers at any time from the same link in your Day-5 email.</li>
              </ul>

              <h3 className="text-base font-bold text-zinc-900 mt-4 mb-2">11.3 Who else sees this data</h3>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Only our transactional email provider (Resend, so the emails actually arrive) and, if we ever
                have to defend a spam complaint or subpoena, our legal advisors. It is <strong>never</strong> sold,
                shared with advertisers or used to train third-party models.
              </p>

              <h3 className="text-base font-bold text-zinc-900 mt-4 mb-2">11.4 Retention</h3>
              <p className="text-sm text-zinc-700 leading-relaxed">
                While you remain subscribed, plus <strong>24 months after you unsubscribe</strong> so we can prove
                compliance with the Spam Act 2003 if a complaint is filed, then the record is hard-deleted.
                You can request earlier deletion using the mechanism below.
              </p>

              <h3 className="text-base font-bold text-zinc-900 mt-4 mb-2">11.5 Your rights (APP 12 &amp; APP 13)</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-zinc-700 leading-relaxed">
                <li><strong>One-click unsubscribe</strong> from every email footer.</li>
                <li><strong>Access</strong>: request a copy of everything we hold about you.</li>
                <li><strong>Correction</strong>: ask us to fix any inaccurate detail.</li>
                <li><strong>Deletion</strong>: ask us to hard-delete your record. We do this within 30 days (the statutory Privacy Act response window) after verifying it's really you asking.</li>
              </ul>
              <p className="text-sm text-zinc-700 leading-relaxed mt-2">
                To exercise any of these rights, email{' '}
                <a href="mailto:recovery@tudor8.com" className="underline">recovery@tudor8.com</a>{' '}
                or submit the request from the course sign-up page. Delete requests immediately halt any
                further emails while identity verification is under way.
              </p>

              <h3 className="text-base font-bold text-zinc-900 mt-4 mb-2">11.6 Deliverability</h3>
              <p className="text-sm text-zinc-700 leading-relaxed">
                Course emails are sent from <code className="text-xs bg-zinc-100 px-1 rounded">recovery@tudor8.com</code>,
                authenticated with SPF, DKIM and DMARC. Replies land in a monitored inbox and are usually
                answered within one business day.
              </p>
            </section>

            <section data-testid="analytics-privacy-section">
              <h2 className="text-xl font-bold text-zinc-950 mb-3">11a. Analytics</h2>
              <div className="text-sm text-zinc-700 leading-relaxed space-y-3">
                <p>
                  Axiom Recovery Programme uses PostHog for anonymous pageview counts only. Data is stored on PostHog's United States servers.
                </p>
                <p>
                  PostHog is configured with in-memory persistence: it uses no cookie and no localStorage entry. Visitors are counted per browser session, not tracked across visits. This matches section 8: we use no analytics cookies.
                </p>
                <p>
                  We do not record sessions. We do not capture clicks, keystrokes, form values or the content of any page. Session recording, autocapture, surveys, dead-click detection, heatmaps and web-vitals telemetry are all switched off. Only the URL path of pages visited is recorded, with all on-page text masked.
                </p>
                <p>
                  The Quick check-in is exempt from every form of analytics. Your answers are scored on your device and never leave the browser.
                </p>
              </div>
            </section>

            <section data-testid="browser-storage-privacy-section">
              <h2 className="text-xl font-bold text-zinc-950 mb-3">11c. What stays in your browser</h2>
              <div className="text-sm text-zinc-700 leading-relaxed space-y-3">
                <p>
                  We keep a small number of items in your browser's local storage so you don't have to sign in or set your preferences again on every visit. None of these items is sent to any other company.
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>a sign-in token so you stay signed in</li>
                  <li>the current Programme week number (used by the games timer)</li>
                  <li>a note that you have seen the medical disclaimer</li>
                  <li>a note that you have passed the invite screen</li>
                  <li>your invite code</li>
                  <li>the language you chose</li>
                  <li>your cookie banner choice</li>
                  <li>your country code, so Help shows services where you live</li>
                  <li>when to ask for your country again</li>
                  <li>your favourite meetings and meeting reminders</li>
                  <li>the games timer and cooldown</li>
                  <li>a dictionary for the word game</li>
                  <li>a note that you closed the "Install Axiom" banner</li>
                </ul>
                <p>
                  Your name, email, sobriety date, check-in answers, mood scores, relapse plan and meeting log are not kept in your browser. They stay on our server. No analytics data is kept in your browser.
                </p>
              </div>
            </section>

            <section data-testid="ai-processors-privacy-section">
              <h2 className="text-xl font-bold text-zinc-950 mb-3">11b. AI processors</h2>
              <div className="text-sm text-zinc-700 leading-relaxed space-y-3">
                <p>
                  Axiom Recovery Programme uses AI services from three companies. Requests are routed through Emergent's Universal Key. We only send the data listed below and only for the feature named.
                </p>

                <p><strong>OpenAI (United States)</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Sponsor Bot: your chat messages to the AI Sponsor Bot are sent to OpenAI (model gpt-5.2) to generate a reply.</li>
                  <li>Voice journals: the audio file you upload is sent to OpenAI (model whisper-1) to produce a written transcript.</li>
                  <li>Read-aloud: the text of a lesson, meeting note or letter you tap "read aloud" on is sent to OpenAI (model tts-1) to generate the audio.</li>
                  <li>Small translations: short in-app text (for example, one lesson snippet at a time) may be sent to OpenAI (model gpt-4o-mini) for translation before the read-aloud step.</li>
                </ul>

                <p><strong>Anthropic (United States)</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>AI Chef: the ingredients, dietary flags, allergies and cuisines you enter are sent to Anthropic (model claude-sonnet-4-6) to generate 5 recipe suggestions.</li>
                  <li>Group-chat moderation: every message you post in a group chat is sent to Anthropic (model claude-haiku-4-5-20251001) to check for glorification of use, cross-talk, self-harm signals and slurs.</li>
                </ul>

                <p><strong>Google (United States)</strong></p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Lesson translations: the English HTML of a lesson is sent to Google (model gemini-2.5-flash) to produce French, Italian, Korean, Mandarin, Portuguese, Russian, Spanish and Ukrainian versions when a SuperAdmin triggers bulk translation.</li>
                  <li>Group-message translation: if your app language differs from the language a group message was written in, that message is sent to Google (same model) to produce a translation on the fly.</li>
                </ul>

                <p>
                  The Quick check-in is exempt from every AI processor. Your PHQ-2, GAD-2 and C-SSRS answers are scored on your device and never leave the browser.
                </p>
              </div>
            </section>

            <section data-testid="quick-checkin-privacy-section">
              <h2 className="text-xl font-bold text-zinc-950 mb-3">12. Quick check-in: privacy and limits</h2>
              <div className="text-sm text-zinc-700 leading-relaxed space-y-3">
                <p>
                  Axiom Recovery Programme is not a crisis service and does not give medical advice. The check-in is not a diagnosis.
                </p>
                <p>
                  Your answers are scored on your device. They are not stored and nobody sees them. The app will not contact you or anyone else about your results.
                </p>
                <p>
                  We keep one anonymous count of how many check-ins show green, amber or red. No name, email or account details are attached. We use it only to understand how many people in the programme may need extra support.
                </p>
                <p>
                  If you are in danger, contact emergency services.
                </p>
              </div>
            </section>
          </div>

          <Button
            onClick={() => navigate(-1)}
            className="mt-6 bg-zinc-950 text-white hover:bg-zinc-800 rounded-md h-12"
          >
            Back
          </Button>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default PrivacyPolicyPage;